/**
 * liveBoardFlag.js — whether the LIVE BOARD nav tab is offered.
 *
 * 🔴 DEFAULT-ON SINCE AUG 26: THE LIVE BOARD IS A PUBLIC BETA. It was gated
 * while it was finished enough for Ben to run his own auction on and not
 * finished enough to put in front of every visitor. It has now run a real
 * ten-team auction end to end, so the tab ships to everyone with a BETA badge
 * beside it (Header.jsx, the same badge the Almanac carries).
 *
 * 🔴 THE MODULE STAYS, AND SO DOES ITS OFF SWITCH. `?live=0` still turns the
 * entry point off for a browser and persists that choice, which is the way
 * back if the beta has to be pulled without a deploy. `?live=1` still works
 * and is now a no-op on a stock profile. The stored key therefore means
 * "explicitly off", not "explicitly on" — a profile that already had '1' from
 * the gated period reads as on, unchanged.
 *
 * 🔴 IT STILL GUARDS TWO THINGS AND NOTHING ELSE: the nav button, and the
 * resume/route path that can set appMode to "live" on its own. Everything
 * downstream of those is unchanged.
 *
 * 🔴 IT MUST NEVER REACH /watch. That route is matched by pathname in App.js
 * and returns before AuctionDraftBuilder — and therefore before this flag is
 * ever read. Eleven people open that link in profiles that have never seen
 * this app. A spectator feed that goes blank on draft night is the worst
 * possible outcome of a change meant to reduce risk, so nothing in this module
 * is imported by the watch path, and a test asserts the page renders with
 * storage empty.
 */
export const LIVE_FLAG_KEY = 'auctionArchitect_liveBoardEnabled';

// '0' is the only stored value that means anything now: explicitly opted out.
// Anything else — absent, or the '1' a gated-period profile still carries — is
// on.
function read() {
  try {
    return localStorage.getItem(LIVE_FLAG_KEY) !== '0';
  } catch {
    return true;                      // private mode / storage disabled: the
                                      // beta is public, so it stays offered
  }
}

function write(on) {
  try {
    if (on) localStorage.removeItem(LIVE_FLAG_KEY);
    else localStorage.setItem(LIVE_FLAG_KEY, '0');
  } catch { /* nothing to do — the choice simply does not persist */ }
}

/**
 * Apply `?live=` if present, then report the resulting state.
 *
 * Call once, at app start. Any value other than "1" or "0" is ignored rather
 * than treated as off, so a stray `?live=` cannot silently switch the beta off
 * for someone.
 */
export function resolveLiveBoardFlag(search) {
  let param = null;
  try {
    const qs = search != null ? search : (typeof window !== 'undefined' ? window.location.search : '');
    param = new URLSearchParams(qs).get('live');
  } catch { /* no URL API / malformed query — fall through to stored state */ }

  if (param === '1') write(true);
  else if (param === '0') write(false);
  return read();
}

/** The stored state, without touching the URL. */
export function isLiveBoardEnabled() {
  return read();
}
